import { useEffect } from "react";
import { View } from "react-native";
import SharedElement from "src/components/SharedElement";
import Shimmer from "src/components/Shimmer";
import { ParsedStickerRowItem } from "src/components/StickerRow/types";
import ToggleImage from "src/components/ToggleImage";
import { useDragSelection } from "src/contexts/DragSelectionContext";
import { useGatheringContext, useGatheringStore } from "src/contexts/GatheringContext";
import parseGatheringPreviewTemplateUrl from "src/helpers/parseV0TemplateUrl/parseGatheringPreviewTemplateUrl";
import type { GatheringOrLocalPhoto } from "src/hooks/useGatheringPhotosWithLocal";
import usePalette from "src/hooks/usePalette";
import { radiusSmall } from "src/styles/radius";
import usePresentPhoto from "../../hooks/usePresentPhoto";
import GatheringHomePhotoOverlay from "./components/GatheringHomePhotoOverlay";

const selectPhoto = (
	store: ReturnType<typeof useGatheringContext>["store"],
	photo: GatheringOrLocalPhoto,
) => {
	store.getState().selectPhoto({
		photoId: photo.photoId,
		ownerId: photo.ownerId,
		readUrl: photo.readUrl,
		variant: photo.variant,
		isTopPhoto: photo.isTopPhoto ?? false,
		state: photo.state,
	});
};
const deselectPhoto = (
	store: ReturnType<typeof useGatheringContext>["store"],
	photo: GatheringOrLocalPhoto,
) => {
	store.getState().deselectPhoto({
		photoId: photo.photoId,
	});
};

const gatheringHomePhotoRadius = radiusSmall;
const GatheringHomePhoto = ({
	photo,
	sticker,
	width,
}: {
	photo: GatheringOrLocalPhoto;
	sticker: ParsedStickerRowItem | null;
	width: number;
}) => {
	const isSelected = useGatheringStore((state) =>
		state.selectedPhotos.hasOwnProperty(photo.photoId),
	);
	const isSingleSelected = useGatheringStore(
		(state) => state.photoSingleSelectedLookup[photo.photoId],
	);
	const selectMode = useGatheringStore((state) => state.selectMode);
	const setSingleSelected = useGatheringStore((state) => state.setSingleSelected);
	const { store } = useGatheringContext();
	const {
		elementRef,
		isSelected: isDragSelected,
		isDragging,
		enabled: dragSelectionEnabled,
	} = useDragSelection();

	const togglePhotoSelect = () => {
		const { setSingleSelected } = store.getState();
		if (isSelected) {
			setSingleSelected(photo.photoId, false);
			deselectPhoto(store, photo);
		} else {
			setSingleSelected(photo.photoId, true);
			selectPhoto(store, photo);
		}
	};
	const presentPhoto = usePresentPhoto();

	useEffect(() => {
		if (!dragSelectionEnabled) {
			setSingleSelected(photo.photoId, false);
			return;
		}

		if (isDragging) {
			if (isDragSelected) {
				selectPhoto(store, photo);
			} else if (!isDragSelected && !isSingleSelected) {
				deselectPhoto(store, photo);
			}
		} else {
			if (!isSingleSelected && isSelected) {
				setSingleSelected(photo.photoId, true);
			}
		}
	}, [
		isSelected,
		isDragging,
		isDragSelected,
		isSingleSelected,
		dragSelectionEnabled,
		setSingleSelected,
		photo,
		store,
	]);
	const imageSrc = parseGatheringPreviewTemplateUrl({
		readUrl: photo.readUrl,
	});
	const { primary } = usePalette();

	const Content = (
		<>
			{!!imageSrc ? (
				<ToggleImage
					onPress={() => {
						if (selectMode === "None") {
							presentPhoto({ photoId: photo.photoId });
						} else {
							togglePhotoSelect();
						}
					}}
					toggleVariant={selectMode === "Select" ? "MultiSelect" : undefined}
					isSelected={isSelected}
					alt="Image"
					src={imageSrc}
					webLoadingBehavior="eager"
					containerStyle={{
						borderRadius: gatheringHomePhotoRadius,
						backgroundColor: `${primary}50`,
						overflow: "hidden",
					}}
					dim={{
						width,
						height: (4 / 3) * width,
					}}
					recyclingKey={photo.photoId}
				/>
			) : (
				<Shimmer
					colorVariant="Card"
					style={{
						width: "100%",
						height: "100%",
						borderRadius: gatheringHomePhotoRadius,
					}}
				/>
			)}
			<GatheringHomePhotoOverlay
				photo={photo}
				sticker={sticker}
				borderRadius={gatheringHomePhotoRadius}
			/>
		</>
	);

	return (
		<View
			testID="GatheringHomePhoto"
			style={{
				// Used to create space between images
				justifyContent: "center",
				alignItems: "center",
			}}
			ref={elementRef}
		>
			<SharedElement
				key={photo.photoId}
				sharedElementId={photo.photoId}
				width={width}
				height={(4 / 3) * width}
			>
				{Content}
			</SharedElement>
		</View>
	);
};
export default GatheringHomePhoto;
